import React, { useEffect, useState, useRef } from 'react';
import { CheckCircleIcon, ClockIcon, XIcon } from 'lucide-react';
interface Notification {
  id: number;
  studentName: string;
  studentInitials: string;
  assessmentTitle: string;
  timestamp: string;
  isRead: boolean;
  type: 'submission' | 'late' | 'incomplete';
}
const NotificationDropdown = ({
  trigger,
  notificationCount
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([{
    id: 1,
    studentName: 'Sarah Johnson',
    studentInitials: 'SJ',
    assessmentTitle: 'Math Quiz: Algebra Fundamentals',
    timestamp: '2 minutes ago',
    isRead: false,
    type: 'submission'
  }, {
    id: 2,
    studentName: 'Michael Chen',
    studentInitials: 'MC',
    assessmentTitle: 'Science Lab: Chemical Reactions',
    timestamp: '15 minutes ago',
    isRead: false,
    type: 'submission'
  }, {
    id: 3,
    studentName: 'Emma Williams',
    studentInitials: 'EW',
    assessmentTitle: 'English Literature Review',
    timestamp: '1 hour ago',
    isRead: false,
    type: 'submission'
  }, {
    id: 4,
    studentName: 'James Rodriguez',
    studentInitials: 'JR',
    assessmentTitle: 'History: World War II Quiz',
    timestamp: '2 hours ago',
    isRead: true,
    type: 'late'
  }, {
    id: 5,
    studentName: 'Olivia Brown',
    studentInitials: 'OB',
    assessmentTitle: 'Math Quiz: Algebra Fundamentals',
    timestamp: '3 hours ago',
    isRead: true,
    type: 'submission'
  }]);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const markAsRead = (id: number) => {
    setNotifications(prev => prev.map(notif => notif.id === id ? {
      ...notif,
      isRead: true
    } : notif));
  };
  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({
      ...notif,
      isRead: true
    })));
  };
  const deleteNotification = (id: number) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'submission':
        return <CheckCircleIcon size={16} className="text-[#00B75F]" data-id="element-1818" />;
      case 'late':
        return <ClockIcon size={16} className="text-[#FF9D33]" data-id="element-1819" />;
      default:
        return <CheckCircleIcon size={16} className="text-gray-400" data-id="element-1820" />;
    }
  };
  const unreadCount = notifications.filter(n => !n.isRead).length;
  return <div className="relative" ref={dropdownRef} data-id="element-1821">
      <div onClick={() => setIsOpen(!isOpen)} data-id="element-1822">{trigger}</div>
      {isOpen && <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-[500px] overflow-hidden flex flex-col" data-id="element-1823">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-1824">
            <div data-id="element-1825">
              <h3 className="text-sm font-semibold text-gray-800" data-id="element-1826">
                Notifications
              </h3>
              {unreadCount > 0 && <p className="text-xs text-gray-500" data-id="element-1827">
                  {unreadCount} unread{' '}
                  {unreadCount === 1 ? 'update' : 'updates'}
                </p>}
            </div>
            {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs text-[#2563EB] hover:text-[#1d4ed8] font-medium" data-id="element-1828">
                Mark all read
              </button>}
          </div>
          {/* Notifications List */}
          <div className="overflow-y-auto flex-1" data-id="element-1829">
            {notifications.length === 0 ? <div className="px-4 py-8 text-center" data-id="element-1830">
                <p className="text-sm text-gray-500" data-id="element-1831">No notifications yet</p>
              </div> : notifications.map(notification => <div key={notification.id} className={`px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${!notification.isRead ? 'bg-blue-50 bg-opacity-30' : ''}`} data-id="element-1832">
                  <div className="flex items-start space-x-3" data-id="element-1833">
                    {/* Avatar */}
                    <div className="w-9 h-9 bg-[#2563EB] rounded-full flex items-center justify-center text-white text-xs font-medium flex-shrink-0" data-id="element-1834">
                      {notification.studentInitials}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-1835">
                      <div className="flex items-start justify-between" data-id="element-1836">
                        <div className="flex-1 min-w-0" data-id="element-1837">
                          <p className="text-sm text-gray-800" data-id="element-1838">
                            <span className="font-medium" data-id="element-1839">
                              {notification.studentName}
                            </span>{' '}
                            submitted
                          </p>
                          <p className="text-xs text-gray-600 truncate mt-0.5" data-id="element-1840">
                            {notification.assessmentTitle}
                          </p>
                        </div>
                        {/* Delete button */}
                        <button onClick={() => deleteNotification(notification.id)} className="ml-2 p-1 rounded-full hover:bg-gray-200 flex-shrink-0" aria-label="Delete notification" data-id="element-1841">
                          <XIcon size={14} className="text-gray-500" data-id="element-1842" />
                        </button>
                      </div>
                      {/* Footer */}
                      <div className="flex items-center justify-between mt-2" data-id="element-1843">
                        <div className="flex items-center space-x-1" data-id="element-1844">
                          {getTypeIcon(notification.type)}
                          <span className="text-xs text-gray-500" data-id="element-1845">
                            {notification.timestamp}
                          </span>
                        </div>
                        {!notification.isRead && <button onClick={() => markAsRead(notification.id)} className="text-xs text-[#2563EB] hover:text-[#1d4ed8] font-medium" data-id="element-1846">
                            Mark read
                          </button>}
                      </div>
                    </div>
                  </div>
                </div>)}
          </div>
          {/* Footer */}
          {notifications.length > 0 && <div className="px-4 py-2 border-t border-gray-200 bg-gray-50" data-id="element-1847">
              <button className="w-full text-center text-xs text-[#2563EB] hover:text-[#1d4ed8] font-medium py-1" data-id="element-1848">
                View all notifications
              </button>
            </div>}
        </div>}
    </div>;
};
export default NotificationDropdown;